import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "bilan2vie_cookie_consent_v1";

/**
 * Cookie consent banner — minimal informational variant (we only use a session cookie).
 * Stores the user's acknowledgement so the banner does not re-appear.
 */
export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay slightly so it doesn't fight with the hero animation
    const t = setTimeout(() => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) {
          setVisible(true);
        }
      } catch (_e) { /* private browsing: just skip */ }
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ accepted: true, ts: Date.now() })); } catch (_e) {}
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-3 left-3 right-3 md:bottom-5 md:left-auto md:right-5 md:max-w-md z-[60] md:mb-0 mb-24"
          data-testid="cookie-banner"
          role="dialog"
          aria-live="polite"
          aria-label="Information sur les cookies"
        >
          <div className="rounded-2xl border border-white/15 bg-[#261B3B]/95 backdrop-blur-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] p-5 md:p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-[#D926A9]/15 border border-[#D926A9]/30 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-4 h-4 text-[#D926A9]" />
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-base text-[#F7F3FC] mb-1">Cookies essentiels</h3>
                <p className="text-[#B59ED1] text-xs md:text-sm leading-relaxed">
                  On utilise uniquement un <strong className="text-[#E2D5F8]">cookie de session</strong> pour te garder connecté. Aucun cookie publicitaire, aucun tracking. Tes données restent privées.{" "}
                  <Link to="/politique-de-confidentialite" className="text-[#00E5FF] hover:underline whitespace-nowrap" data-testid="cookie-banner-link">
                    En savoir plus
                  </Link>
                </p>
              </div>
              <button
                onClick={accept}
                aria-label="Fermer la bannière cookies"
                className="text-[#B59ED1] hover:text-white p-1 -mt-1 -mr-1"
                data-testid="cookie-banner-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={accept}
              className="w-full px-4 py-2 rounded-full gradient-primary text-sm font-medium text-white hover:shadow-[0_0_20px_rgba(217,38,169,0.4)] transition-all"
              data-testid="cookie-banner-accept"
            >
              J'ai compris
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
